import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@eisox/design-system";

import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { Card } from "./card";

interface RecoveryWheelProps extends ComponentPropsWithoutRef<"div"> {
  speed?: number;
  efficiency?: number;
  defect?: boolean;
  missmatchDefect?: boolean;
}

const LINE_HEIGHT = 9.0567;
const LINES_COUNT = 20;
const TOTAL_HEIGHT = LINE_HEIGHT * LINES_COUNT; // ≈ 181.13px

const RecoveryWheel: React.FC<RecoveryWheelProps> = ({
  speed = 0,
  efficiency,
  defect,
  missmatchDefect,
  className,
  ...props
}) => {
  const duration = speed > 0 ? `${200 / speed}s` : undefined;
  const formattedEfficiency = efficiency?.toFixed(1) ?? "--";

  return (
    <div
      {...props}
      className={cn(
        "gap-1.25 relative my-auto flex w-fit flex-col items-center [grid-area:recoveryWheel]",
        props.onClick && "cursor-pointer",
        className,
      )}
    >
      <Tooltip content={m.air_handler_unit_recovery_wheel_efficiency()} position="top">
        <Card className={cn("-top-2.5 absolute -translate-y-full")} defect={defect || missmatchDefect}>
          {`${formattedEfficiency} %`}
        </Card>
      </Tooltip>
      <svg width="61" height="228" viewBox="0 0 61 228" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M4.60059 0.351562H56.1553C58.2928 0.351562 60.0264 2.08511 60.0264 4.22266V223.777C60.0264 225.915 58.2928 227.648 56.1553 227.648H4.60059C2.46304 227.648 0.729492 225.915 0.729492 223.777V4.22266C0.729492 2.08511 2.46304 0.351562 4.60059 0.351562Z"
          fill="#F3F3F9"
        />
        <path
          d="M4.60059 0.351562H56.1553C58.2928 0.351562 60.0264 2.08511 60.0264 4.22266V223.777C60.0264 225.915 58.2928 227.648 56.1553 227.648H4.60059C2.46304 227.648 0.729492 225.915 0.729492 223.777V4.22266C0.729492 2.08511 2.46304 0.351562 4.60059 0.351562Z"
          stroke="#122138"
          strokeWidth="0.703704"
        />
        <rect x="2.58154" y="109.803" width="55.5926" height="8.44444" fill="#122138" />
        <g clipPath="url(#clip0_24390_43668)">
          <rect x="9.61865" y="14.0991" width="40.8148" height="199.852" fill="#D0D0D0" />
          <g className={cn("recovery-wheel-moving-lines")} style={{ animationDuration: duration }}>
            {[...Array.from({ length: 2 })].flatMap((_, dupIndex) =>
              Array.from({ length: LINES_COUNT }, (_, i) => {
                const y = 22.8034 + i * LINE_HEIGHT + dupIndex * TOTAL_HEIGHT;
                return (
                  <line
                    key={`${dupIndex}-${i}`}
                    opacity="0.7"
                    x1="10.3228"
                    y1={y}
                    x2="49.9501"
                    y2={y}
                    stroke="#122138"
                    strokeWidth="0.703704"
                  />
                );
              }),
            )}
          </g>
        </g>
        <rect x="9.9705" y="14.451" width="40.1111" height="199.148" stroke="#122138" strokeWidth="0.703704" />
        <defs>
          <clipPath id="clip0_24390_43668">
            <rect x="9.61865" y="14.0991" width="40.8148" height="199.852" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

RecoveryWheel.displayName = "RecoveryWheel";

export { RecoveryWheel };
export type { RecoveryWheelProps };
